import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { useStudioCounts } from '../../api/hooks';
import { ProfileDialog } from '../../components/dialogs/ProfileDialog';
import { AreaSwitcher, Brand, SessionUserMenu, useHeaderHeight } from '../../components/Header';
import { Icon, type IconName } from '../../components/Icon';
import { Ico } from '../../components/Sidebar';
import { fmtCount } from '../../lib/format';
import { useSession } from '../../state/session';
import { NoAccess } from '../NoAccess';
import { AccessPage } from './AccessPage';
import { ActivityPage } from './ActivityPage';
import { AlbumPage } from './AlbumPage';
import { AlbumsPage } from './AlbumsPage';
import { DuplicatesPage } from './DuplicatesPage';
import { RemovalRequestsPage } from './RemovalRequestsPage';
import { StudioPhotosView } from './StudioPhotosView';
import { TrashPage } from './TrashPage';
import { UploadPage } from './UploadPage';

/** Studio header: logo + "Studio", area switcher, Upload, avatar. */
function StudioHeader({ onMenu }: { onMenu(): void }) {
  const { me } = useSession();
  const ref = useRef<HTMLElement>(null);
  const [profile, setProfile] = useState(false);
  useHeaderHeight(ref);
  return (
    <header className="top" ref={ref}>
      <div className="top-row studio-row">
        <div className="top-left">
          <button type="button" className="menu-btn" aria-label="Open navigation" onClick={onMenu}>
            <Icon name="menu" />
          </button>
          <Brand to="/studio" label="Studio" />
        </div>
        <div className="actions">
          {/* Upload sits left of the area switcher, so the switcher keeps its place in every area. */}
          {me.capabilities.media && (
            <Link className="btn primary" to="/studio/upload">
              <Icon name="upload" />
              <span className="hide-sm">Upload</span>
            </Link>
          )}
          <AreaSwitcher current="studio" />
          <SessionUserMenu onProfile={() => setProfile(true)} />
        </div>
      </div>
      {profile && <ProfileDialog onClose={() => setProfile(false)} />}
    </header>
  );
}

/**
 * Studio left nav: Albums; Photos (Unreleased, Recently uploaded, Restricted, Possible duplicates, Trash); Review
 * (Removal requests); Manage (Album access, Activity). An admin who isn't media sees only Album access.
 */
function StudioNav({ open, onClose }: { open: boolean; onClose(): void }) {
  const { access } = useSession();
  const counts = useStudioCounts(access.hasStudio);
  const location = useLocation();
  const navigate = useNavigate();
  const c = counts.data;
  const item = (to: string, nav: string, icon: IconName, label: string, count?: number, badge?: boolean) => {
    const on = to === '/studio/albums' ? location.pathname.startsWith('/studio/albums') : location.pathname === to;
    return (
      <button
        type="button"
        className={`sn${on ? ' on' : ''}`}
        aria-current={on ? 'page' : undefined}
        onClick={() => {
          onClose();
          window.scrollTo(0, 0);
          navigate(to);
        }}
      >
        <Ico nav={nav} icon={icon} />
        <span>{label}</span>
        {count != null && (badge ? count > 0 && <span className="sbadge">{count}</span> : <span className="n">{fmtCount(count)}</span>)}
      </button>
    );
  };
  return (
    <>
      <aside className={`side${open ? ' open' : ''}`} aria-label="Studio navigation">
        {access.hasStudio && (
          <>
            {item('/studio/albums', 'studio:albums', 'folder', 'Albums', c?.albums)}
            <div className="sl">Photos</div>
            {item('/studio/unreleased', 'studio:unreleased', 'eyeOff', 'Unreleased', c?.unreleased)}
            {item('/studio/recent', 'studio:recent', 'clock', 'Recently uploaded')}
            {access.seesRestricted && item('/studio/restricted', 'studio:restricted', 'lock', 'Restricted', c?.restricted)}
            {access.isMedia && item('/studio/duplicates', 'studio:duplicates', 'merge', 'Possible duplicates', c?.duplicates)}
            {access.isMedia && item('/studio/trash', 'studio:trash', 'trash', 'Trash', c?.trash)}
            {access.isMedia && (
              <>
                <div className="sl">Review</div>
                {item('/studio/removals', 'studio:removals', 'shield', 'Removal requests', c?.removalRequests, true)}
              </>
            )}
          </>
        )}
        {(access.canManageAccess || access.hasStudio) && <div className="sl">Manage</div>}
        {access.canManageAccess && item('/studio/access', 'studio:access', 'users', 'Album access')}
        {access.hasStudio && item('/studio/activity', 'studio:activity', 'undo', 'Activity')}
        <div className="sfoot">
          {access.isMedia
            ? 'Unreleased photos are only visible here until someone releases them.'
            : access.hasStudio
              ? 'You can upload and tag in the albums you were given access to.'
              : 'As an admin you can manage who has access to which album.'}
        </div>
      </aside>
      <div className={`scrim${open ? ' open' : ''}`} onClick={onClose} />
    </>
  );
}

/** `/studio/*`: its own header and left nav, same look as Browse. People without Studio access get a plain notice. */
export function StudioApp() {
  const { me, access } = useSession();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  // Studio's darkroom theme: every Studio style is keyed on [data-area="studio"] (styles/app.css), set only while here.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.source = 'glean';
    root.dataset.area = 'studio';
    return () => {
      delete root.dataset.area;
    };
  }, []);
  useEffect(() => setDrawer(false), [location.pathname]);

  if (!me.capabilities.studio) return <NoAccess area="Studio" />;
  // An admin who isn't media (and has no albums) only manages album access here.
  const home = access.hasStudio ? 'albums' : 'access';
  const content = (el: ReactElement) => (access.hasStudio ? el : <Navigate to="/studio/access" replace />);
  return (
    <>
      <StudioHeader onMenu={() => setDrawer(true)} />
      <div className="layout">
        <StudioNav open={drawer} onClose={() => setDrawer(false)} />
        <main className="wrap">
          <Routes>
            <Route index element={<Navigate to={home} replace />} />
            <Route path="albums" element={content(<AlbumsPage />)} />
            <Route path="albums/:id" element={content(<AlbumPage />)} />
            <Route
              path="unreleased"
              element={content(
                <StudioPhotosView
                  key="unreleased"
                  title="Unreleased"
                  fixedVisibility="unreleased"
                  notice="Waiting for review in every album you can open. Select photos and choose Release to show them to everyone signed in."
                  empty="Nothing is waiting to be released."
                />,
              )}
            />
            <Route
              path="recent"
              element={content(<StudioPhotosView key="recent" title="Recently uploaded" defaultSort="uploaded" empty="Nothing has been uploaded yet." />)}
            />
            <Route
              path="restricted"
              element={
                access.seesRestricted && access.hasStudio ? (
                  <StudioPhotosView
                    key="restricted"
                    title="Restricted"
                    fixedVisibility="restricted"
                    notice="Only people allowed to see restricted photos can see these. They can’t be shared by link."
                    empty="No restricted photos."
                  />
                ) : (
                  <NoAccess area="Restricted photos" inline />
                )
              }
            />
            <Route path="removals" element={access.isMedia ? <RemovalRequestsPage /> : <NoAccess area="Removal requests" inline />} />
            <Route path="duplicates" element={access.isMedia && access.hasStudio ? <DuplicatesPage /> : <NoAccess area="Possible duplicates" inline />} />
            <Route path="trash" element={access.isMedia ? <TrashPage /> : <NoAccess area="Trash" inline />} />
            <Route path="activity" element={content(<ActivityPage />)} />
            <Route path="upload" element={content(<UploadPage />)} />
            <Route path="access" element={access.canManageAccess ? <AccessPage /> : <NoAccess area="Album access" inline />} />
            <Route path="*" element={<Navigate to={home} replace />} />
          </Routes>
        </main>
      </div>
    </>
  );
}
